import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Inbox, Phone, Mail, ExternalLink, StickyNote, Save, CheckCircle2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { API_BASE_URL } from '@/utils/env';
import { getFreshToken } from '@/utils/auth';
import {
  REQUEST_KINDS, REQUEST_KIND_BY_ID, REQUEST_STATUSES, STATUS_LABEL, normStatus,
} from '@/config/serviceRequests';

/* Admin Requests — the single queue for every "talk to us" form a client can
   send from their dashboard: consultations, tax & corporate filings, corporate
   services and external audit. One tab per kind (plus All), each row carrying
   the client, what they asked for, a status the admin drives and a private note.
   Reads GET /auth/admin/service-requests, PATCHes /auth/admin/service-requests/:uid/:id.
   Legacy consultation requests stored on the user record are folded into the
   same list by the backend, so nothing submitted before this page existed is
   stranded. Scoped under `.adminx` like the rest of the panel. */

type Req = {
  id: string;
  uid: string;
  kind?: string;
  topic?: string;
  message?: string;
  phone?: string;
  name?: string;
  email?: string;
  company?: string;
  status?: string;
  adminNote?: string;
  requestedAt?: string;
  legacy?: boolean;
};

const fmtDate = (v?: string) => {
  if (!v) return '-';
  try { return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return v; }
};
const dayOf = (v?: string) => (v || '').slice(0, 10);

const initials = (r: Req) => {
  const n = (r.name || '').trim();
  if (n) return n.split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  const em = (r.email || '').trim();
  return em ? em.slice(0, 2).toUpperCase() : '?';
};

const AV_COLORS = ['#DBEAFE|#1D4ED8', '#EEF2F6|#475569', '#E9F7EF|#15803D', '#FEF6E7|#B45309', '#FDF2F8|#BE185D'];
const avStyle = (key: string) => {
  const sum = [...key].reduce((a, c) => a + c.charCodeAt(0), 0);
  const [background, color] = (AV_COLORS[sum % AV_COLORS.length] || AV_COLORS[0]!).split('|');
  return { background, color };
};

const CHEV = "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%2364748B' stroke-width='1.5'/%3E%3C/svg%3E\")";

const CSS = `
/* Page-specific only — tokens, cards, tabs and buttons come from adminShell.css. */
.adminx .rq-tabs{margin-bottom:18px}
.adminx .rq-tabs .tab .n{display:inline-grid;place-items:center;min-width:19px;height:19px;padding:0 5px;border-radius:999px;background:#DDE2EA;color:#475569;font-size:11px;font-weight:700}
.adminx .rq-tabs .tab.on .n{background:var(--brand);color:var(--brand-on)}
.adminx .rq-tabs .tab .n.hot{background:#FEE2E2;color:#B91C1C}
.adminx .rq-tabs .tab.on .n.hot{background:#fff;color:#B91C1C}

.adminx .rq-bar{display:flex;align-items:flex-end;gap:14px;flex-wrap:wrap}
.adminx .rq-bar .grow{margin-left:auto}
.adminx .fld{display:flex;flex-direction:column;gap:6px;min-width:0}
.adminx .fld label{font-size:11px;font-weight:600;letter-spacing:.6px;text-transform:uppercase;color:#64748B}
.adminx .fld input,.adminx .fld select{border:1px solid #E6E9ED;border-radius:9px;padding:9px 12px;font-size:13px;background:#fff;width:100%;font-family:inherit;color:#0F172A}
.adminx .fld select{-webkit-appearance:none;appearance:none;padding-right:30px;background-image:${CHEV};background-repeat:no-repeat;background-position:right 11px center}
.adminx .fld input:focus,.adminx .fld select:focus{border-color:var(--brand);outline:none;box-shadow:0 0 0 3px var(--brand-soft)}
.adminx .fld input::placeholder{color:#94A3B8}
.adminx .rq-count{font-size:12px;color:#94A3B8;margin:14px 2px 12px}

.adminx .rq-list{display:flex;flex-direction:column;gap:12px}
.adminx .rq{background:#fff;border:1px solid var(--ax-line);border-radius:14px;padding:16px 18px;transition:box-shadow .14s}
.adminx .rq:hover{box-shadow:0 2px 10px rgba(15,23,42,.06)}
.adminx .rq.is-new{border-inline-start:3px solid var(--brand)}
.adminx .rq-top{display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap}
.adminx .rq-av{width:38px;height:38px;border-radius:50%;display:grid;place-items:center;font-size:13px;font-weight:600;flex:none}
.adminx .rq-who{min-width:0;flex:1 1 220px}
.adminx .rq-who b{display:block;font-size:14px;font-weight:600;color:#0F172A}
.adminx .rq-who .meta{display:flex;gap:12px;flex-wrap:wrap;margin-top:3px}
.adminx .rq-who .meta a,.adminx .rq-who .meta span{display:inline-flex;align-items:center;gap:5px;font-size:12px;color:#94A3B8;text-decoration:none}
.adminx .rq-who .meta a:hover{color:var(--brand)}
.adminx .rq-who .meta svg{width:12px;height:12px}
.adminx .rq-right{display:flex;align-items:center;gap:8px;margin-inline-start:auto}

.adminx .kind{display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:600;padding:4px 10px;border-radius:999px;background:#EFF1F4;color:#475569;white-space:nowrap}
.adminx .kind svg{width:12px;height:12px}
.adminx .when{font-size:12px;color:#94A3B8;white-space:nowrap}

.adminx .rq-body{margin-top:12px;padding-inline-start:50px}
.adminx .rq-topic{font-size:13.5px;font-weight:600;color:#0F172A}
.adminx .rq-msg{font-size:13px;color:#475569;line-height:1.6;margin-top:4px;white-space:pre-wrap}
.adminx .rq-none{font-size:12.5px;color:#CBD5E1;margin-top:4px}
.adminx .rq-note{display:flex;gap:8px;align-items:flex-start;margin-top:6px}
.adminx .rq-note textarea{flex:1;border:1px solid #E6E9ED;border-radius:10px;padding:8px 11px;font:inherit;font-size:12.5px;color:#0F172A;resize:vertical;min-height:38px}
.adminx .rq-note textarea:focus{border-color:var(--brand);outline:none;box-shadow:0 0 0 3px var(--brand-soft)}
.adminx .rq-note-lbl{display:flex;align-items:center;gap:5px;margin-top:14px;font-size:11px;font-weight:600;letter-spacing:.5px;text-transform:uppercase;color:#94A3B8}
.adminx .rq-note-lbl svg{width:12px;height:12px}
.adminx .saved{display:inline-flex;align-items:center;gap:4px;font-size:11.5px;font-weight:700;color:#15803D}
.adminx .saved svg{width:13px;height:13px}

.adminx .status-select{-webkit-appearance:none;appearance:none;border:1px solid #E6E9ED;border-radius:999px;padding:5px 26px 5px 12px;font:inherit;font-size:12px;font-weight:600;cursor:pointer;background-image:${CHEV};background-repeat:no-repeat;background-position:right 10px center;transition:.14s}
.adminx .status-select:focus{outline:none;box-shadow:0 0 0 3px var(--brand-soft)}
.adminx .status-select:disabled{opacity:.6;cursor:default}
.adminx .status-select option{color:#0F172A;background:#fff}
.adminx .status-select[data-s="new"]{color:#B45309;background-color:#FEF6E7;border-color:transparent}
.adminx .status-select[data-s="contacted"],.adminx .status-select[data-s="scheduled"]{color:var(--brand-d);background-color:var(--brand-soft);border-color:transparent}
.adminx .status-select[data-s="done"]{color:#15803D;background-color:#E9F7EF;border-color:transparent}
.adminx .status-select[data-s="cancelled"]{color:#94A3B8;background-color:#F1F5F9;border-color:transparent}

.adminx .rq-open{display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;color:var(--brand-d);text-decoration:none;white-space:nowrap}
.adminx .rq-open:hover{text-decoration:underline}
.adminx .rq-open svg{width:12px;height:12px}

.adminx .rq-loading{display:flex;justify-content:center;padding:70px 0}
/* Column-centred: the shared .empty only sets text-align, which leaves the icon
   box and the max-width paragraph — both blocks — pinned to the left edge. */
.adminx .rq-empty{display:flex;flex-direction:column;align-items:center;gap:8px}
.adminx .rq-empty .ico{width:44px;height:44px;border-radius:12px;background:#EEF2F6;color:#94A3B8;display:grid;place-items:center;flex:none}
.adminx .rq-empty .ico svg{width:20px;height:20px;margin:0}
.adminx .rq-empty b{font-size:14px;color:#0F172A;margin:0}
.adminx .rq-empty p{font-size:12.5px;color:#64748B;max-width:380px}

@media(max-width:700px){
  .adminx .rq-body{padding-inline-start:0}
  .adminx .rq-right{margin-inline-start:0;width:100%}
}
`;

export default function AdminServiceRequests() {
  const [reqs, setReqs] = useState<Req[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<string>('all');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  // Note drafts, keyed by request — typing must not fight the loaded value.
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [noteSaved, setNoteSaved] = useState<string | null>(null);

  const key = (r: Req) => `${r.uid}/${r.id}`;

  const load = async () => {
    setLoading(true); setError('');
    try {
      const token = await getFreshToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/service-requests`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      // A 404 here means the backend on this environment predates the requests
      // endpoints — the raw "Endpoint not found" tells an admin nothing.
      if (res.status === 404) throw new Error('The requests service isn\'t available on this environment yet — the backend needs deploying.');
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load requests');
      const list: Req[] = json.requests || [];
      setReqs(list);
      setNotes(Object.fromEntries(list.map(r => [`${r.uid}/${r.id}`, r.adminNote || ''])));
    } catch (e: any) {
      setError(e.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (r: Req, body: Record<string, unknown>) => {
    setSavingId(key(r));
    const prev = reqs;
    setReqs(cur => cur.map(x => (key(x) === key(r) ? { ...x, ...body } : x))); // optimistic
    try {
      const token = await getFreshToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/service-requests/${r.uid}/${r.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || 'Update failed');
      return true;
    } catch (e: any) {
      setReqs(prev); // back to server truth
      setError(e.message || 'Could not save the change');
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const saveNote = async (r: Req) => {
    const ok = await patch(r, { adminNote: notes[key(r)] ?? '' });
    if (ok) { setNoteSaved(key(r)); setTimeout(() => setNoteSaved(null), 2000); }
  };

  const counts = useMemo(() => {
    const c: Record<string, { total: number; open: number }> = { all: { total: 0, open: 0 } };
    REQUEST_KINDS.forEach(k => { c[k.id] = { total: 0, open: 0 }; });
    reqs.forEach(r => {
      const k = r.kind && c[r.kind] ? r.kind : 'consultation';
      const open = normStatus(r.status) === 'new' ? 1 : 0;
      c.all!.total++; c.all!.open += open;
      if (c[k]) { c[k]!.total++; c[k]!.open += open; }
    });
    return c;
  }, [reqs]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return reqs.filter(r => {
      const kind = r.kind || 'consultation';
      if (tab !== 'all' && kind !== tab) return false;
      if (status && normStatus(r.status) !== status) return false;
      if (from && dayOf(r.requestedAt) && dayOf(r.requestedAt) < from) return false;
      if (s && !`${r.name || ''} ${r.email || ''} ${r.company || ''} ${r.topic || ''} ${r.message || ''} ${r.phone || ''}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [reqs, tab, status, q, from]);

  const openTotal = counts.all?.open || 0;

  return (
    <AdminLayout
      section="Overview"
      title="Requests"
      icon={Inbox}
      subtitle="Consultations, tax & filings, corporate services and audit requests clients send from their dashboard."
      actions={
        <button className="btn" onClick={load} disabled={loading}>
          <RefreshCw className={loading ? 'spin' : ''} /> Refresh
        </button>
      }
    >
      <style>{CSS}</style>

      {error && <div className="errbar">{error}</div>}

      <div className="tabs rq-tabs">
        <button className={`tab${tab === 'all' ? ' on' : ''}`} onClick={() => setTab('all')}>
          All
          <span className={`n${openTotal ? ' hot' : ''}`}>{counts.all?.total || 0}</span>
        </button>
        {REQUEST_KINDS.map(k => {
          const c = counts[k.id] || { total: 0, open: 0 };
          const Icon = k.icon;
          return (
            <button key={k.id} className={`tab${tab === k.id ? ' on' : ''}`} onClick={() => setTab(k.id)}>
              <Icon style={{ width: 14, height: 14 }} />
              {k.label}
              <span className={`n${c.open ? ' hot' : ''}`}>{c.total}</span>
            </button>
          );
        })}
      </div>

      <div className="card">
        <div className="card-b">
          <div className="rq-bar">
            <div className="fld" style={{ minWidth: 240 }}>
              <label htmlFor="rqSearch">Search</label>
              <input id="rqSearch" value={q} onChange={e => setQ(e.target.value)} placeholder="Client, company, topic or message…" />
            </div>
            <div className="fld" style={{ minWidth: 160 }}>
              <label htmlFor="rqStatus">Status</label>
              <select id="rqStatus" value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {REQUEST_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
            <div className="fld">
              <label htmlFor="rqFrom">Requested from</label>
              <input id="rqFrom" type="date" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="grow" />
          </div>
        </div>
      </div>

      {!loading && !error && (
        <div className="rq-count">
          Showing {filtered.length} of {reqs.length} request{reqs.length === 1 ? '' : 's'}
          {openTotal > 0 && <> · <b style={{ color: '#B45309' }}>{openTotal} awaiting a first response</b></>}
        </div>
      )}

      {loading ? (
        <div className="card"><div className="rq-loading"><div className="ax-spinner" /></div></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty rq-empty">
            <span className="ico"><Inbox /></span>
            <b>{reqs.length === 0 ? 'No requests yet' : 'No requests match your filters'}</b>
            <p>
              {reqs.length === 0
                ? 'When a client asks for a consultation, tax filing, corporate services or an audit from their dashboard, it lands here straight away.'
                : 'Try another name or topic, a different status, or clear the date filter.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="rq-list">
          {filtered.map(r => {
            const s = normStatus(r.status);
            const kind = REQUEST_KIND_BY_ID[r.kind || 'consultation'] || REQUEST_KIND_BY_ID.consultation!;
            const KIcon = kind.icon;
            const k = key(r);
            const busy = savingId === k;
            return (
              <div key={k} className={`rq${s === 'new' ? ' is-new' : ''}`}>
                <div className="rq-top">
                  <span className="rq-av" style={avStyle(r.name || r.email || r.uid)}>{initials(r)}</span>
                  <div className="rq-who">
                    <b>{r.name || r.email || 'Client'}{r.company ? ` · ${r.company}` : ''}</b>
                    <div className="meta">
                      {r.email && <a href={`mailto:${r.email}`}><Mail />{r.email}</a>}
                      {r.phone && <a href={`tel:${r.phone.replace(/\s+/g, '')}`}><Phone />{r.phone}</a>}
                      <span className="when">{fmtDate(r.requestedAt)}</span>
                    </div>
                  </div>
                  <div className="rq-right">
                    <span className="kind"><KIcon />{kind.label}</span>
                    <select
                      className="status-select"
                      data-s={s}
                      value={s}
                      disabled={busy}
                      onChange={e => patch(r, { status: e.target.value })}
                      aria-label={`Status for ${r.name || 'request'}`}
                    >
                      {REQUEST_STATUSES.map(x => <option key={x} value={x}>{STATUS_LABEL[x]}</option>)}
                    </select>
                    <Link className="rq-open" to={`/admin/command-center?client=${r.uid}`} title="Open this client in the Command Center">
                      <ExternalLink />Client
                    </Link>
                  </div>
                </div>

                <div className="rq-body">
                  <div className="rq-topic">{r.topic || kind.title}</div>
                  {r.message
                    ? <div className="rq-msg">{r.message}</div>
                    : <div className="rq-none">No message left.</div>}

                  <div>
                    <div className="rq-note-lbl"><StickyNote />Internal note</div>
                    <div className="rq-note">
                      <textarea
                        rows={1}
                        value={notes[k] ?? ''}
                        onChange={e => setNotes(n => ({ ...n, [k]: e.target.value }))}
                        placeholder="Who's handling this, what was agreed…"
                      />
                      {noteSaved === k
                        ? <span className="saved"><CheckCircle2 />Saved</span>
                        : (notes[k] ?? '') !== (r.adminNote || '') && (
                          <button className="btn btn-pri" onClick={() => saveNote(r)} disabled={busy}>
                            <Save /> Save
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
